import React from "react";

import clsx from "clsx";

// AIDEV-NOTE: Two-column auth layout for login/signup/password reset screens
// Left column (40% width on lg+) contains auth form content with dark green background
// Right column (60%) displays promotional/educational content
interface AuthLayoutProps {
    children: React.ReactNode;
    /** Content to display in the right promotional column (only visible on lg+ screens) */
    promotionalContent?: React.ReactNode;
    /** Title displayed at the top of the auth form */
    title?: string;
    /** Optional background image URL for the right column */
    promotionalBackgroundImage?: string;
    /** Logo variant: 'appicon' (default) or 'horizontal' */
    logoVariant?: "appicon" | "horizontal";
    /**
     * Below lg, render the appicon logo inline at the left of the title
     * (vertically centered) instead of stacked above it — reclaims ~80px of
     * vertical space on phones. Desktop keeps the stacked layout.
     */
    inlineLogoTitleOnMobile?: boolean;
}

export default function AuthLayout({
    children,
    promotionalContent,
    title,
    promotionalBackgroundImage,
    logoVariant = "appicon",
    inlineLogoTitleOnMobile = false,
}: AuthLayoutProps) {
    return (
        <div className="bg-brand-cream-100 flex min-h-screen-safe w-full">
            {/* Left Column - Auth Form (40% width on lg+, full width on smaller screens) */}
            <div className="flex w-full flex-col bg-gradient-to-bl from-brand-forest-700 to-brand-forest-900 lg:w-[40%] min-h-screen-safe">
                <div className="flex h-full flex-col items-center justify-center px-6 py-12 lg:px-12 min-h-screen-safe">
                    {/* Content container: centered */}
                    <div className="w-full max-w-md space-y-8">
                        {inlineLogoTitleOnMobile &&
                        title &&
                        logoVariant === "appicon" ? (
                            // AIDEV-NOTE: Compact header — logo inline left of
                            // title below lg, stacked above title on lg+.
                            <div className="flex items-center justify-center gap-3 lg:flex-col lg:gap-8">
                                <img
                                    src="/parts-companion-logo-appicon-light.png"
                                    alt="Parts Companion"
                                    className="h-10 w-10 lg:h-16 lg:w-16"
                                />
                                <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-white lg:w-full lg:text-center lg:text-4xl">
                                    {title}
                                </h1>
                            </div>
                        ) : (
                            <>
                                {/* Logo above title */}
                                <div className="flex justify-center">
                                    {logoVariant === "horizontal" ? (
                                        <img
                                            src="/parts-companion-logomark-horz-light.png"
                                            alt="Parts Companion"
                                            className="w-full object-contain aspect-[3028/1076]"
                                        />
                                    ) : (
                                        <img
                                            src="/parts-companion-logo-appicon-light.png"
                                            alt="Parts Companion"
                                            className="h-12 w-12 lg:h-16 lg:w-16"
                                        />
                                    )}
                                </div>

                                {title && (
                                    <h1 className="w-full text-center font-display text-3xl font-bold leading-tight tracking-tight text-white lg:text-4xl">
                                        {title}
                                    </h1>
                                )}
                            </>
                        )}

                        {children}
                    </div>
                </div>
            </div>

            {/* Right Column - Promotional Content (only visible on lg+ screens) */}
            {promotionalContent && (
                <div
                    className="bg-brand-cream-100 hidden w-[60%] flex-col items-center justify-center p-12 lg:flex"
                    style={
                        promotionalBackgroundImage
                            ? {
                                  backgroundImage: `url('${promotionalBackgroundImage}')`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  backgroundRepeat: "no-repeat",
                              }
                            : undefined
                    }
                >
                    {promotionalContent}
                </div>
            )}
        </div>
    );
}
